import type {
  Candidate,
  CandidateDetail,
  CreateCandidateInput,
  UpdateCandidateInput,
  MoveCandidateStageInput,
  ConvertCandidateInput,
  ListCandidateQuery,
  RecruitmentProcess,
  RecruitmentStepSection,
  RecruitmentStepTemplate,
  UpdateSectionInput,
  RecruitmentProcessInstance,
  RecruitmentProcessInstanceDetail,
  RecruitmentStats,
  CreateStepTemplateInput,
  UpdateStepTemplateInput,
  UpdateProcessSettingsInput,
  StartProcessInput,
  ToggleStepInput,
  ListProcessInstanceQuery,
  ProcessType,
  DataResponse,
  PaginatedResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

export const recruitmentService = {
  async stats(): Promise<RecruitmentStats> {
    const res = await api.get<DataResponse<RecruitmentStats>>('/recruitment/stats');
    return res.data.data;
  },

  // ── Candidates ──
  async listCandidates(query: Partial<ListCandidateQuery> = {}): Promise<PaginatedResponse<Candidate>> {
    const res = await api.get<PaginatedResponse<Candidate>>('/recruitment/candidates', { params: query });
    return res.data;
  },

  async getCandidate(id: string): Promise<CandidateDetail> {
    const res = await api.get<DataResponse<CandidateDetail>>(`/recruitment/candidates/${id}`);
    return res.data.data;
  },

  async createCandidate(input: CreateCandidateInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/recruitment/candidates', input);
    return res.data.data;
  },

  async updateCandidate(id: string, input: UpdateCandidateInput): Promise<void> {
    await api.patch(`/recruitment/candidates/${id}`, input);
  },

  async deleteCandidate(id: string): Promise<void> {
    await api.delete(`/recruitment/candidates/${id}`);
  },

  async moveStage(id: string, input: MoveCandidateStageInput): Promise<void> {
    await api.post(`/recruitment/candidates/${id}/stage`, input);
  },

  async convertCandidate(
    id: string,
    input: ConvertCandidateInput,
  ): Promise<{ user_id: string; instance_id: string | null }> {
    const res = await api.post<DataResponse<{ user_id: string; instance_id: string | null }>>(
      `/recruitment/candidates/${id}/convert`,
      input,
    );
    return res.data.data;
  },

  async uploadResume(id: string, file: File): Promise<void> {
    const form = new FormData();
    form.append('file', file);
    await api.post(`/recruitment/candidates/${id}/resume`, form);
  },

  async downloadResume(id: string): Promise<Blob> {
    const res = await api.get(`/recruitment/candidates/${id}/resume`, { responseType: 'blob' });
    return res.data as Blob;
  },

  // ── Process config ──
  async listProcesses(): Promise<RecruitmentProcess[]> {
    const res = await api.get<DataResponse<RecruitmentProcess[]>>('/recruitment/processes');
    return res.data.data;
  },

  async getProcess(
    type: ProcessType,
  ): Promise<{ process: RecruitmentProcess; sections: RecruitmentStepSection[]; steps: RecruitmentStepTemplate[] }> {
    const res = await api.get<
      DataResponse<{ process: RecruitmentProcess; sections: RecruitmentStepSection[]; steps: RecruitmentStepTemplate[] }>
    >(`/recruitment/processes/${type}`);
    return res.data.data;
  },

  async createSection(processId: string, name: string): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(`/recruitment/processes/${processId}/sections`, { name });
    return res.data.data;
  },

  async updateSection(sectionId: string, input: UpdateSectionInput): Promise<void> {
    await api.patch(`/recruitment/sections/${sectionId}`, input);
  },

  async deleteSection(sectionId: string): Promise<void> {
    await api.delete(`/recruitment/sections/${sectionId}`);
  },

  async reorderSections(processId: string, orderedIds: string[]): Promise<void> {
    await api.post(`/recruitment/processes/${processId}/sections/reorder`, { ordered_ids: orderedIds });
  },

  async updateProcessSettings(id: string, input: UpdateProcessSettingsInput): Promise<void> {
    await api.patch(`/recruitment/processes/${id}`, input);
  },

  async createStep(processId: string, input: CreateStepTemplateInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(`/recruitment/processes/${processId}/steps`, input);
    return res.data.data;
  },

  async updateStep(stepId: string, input: UpdateStepTemplateInput): Promise<void> {
    await api.patch(`/recruitment/steps/${stepId}`, input);
  },

  async deleteStep(stepId: string): Promise<void> {
    await api.delete(`/recruitment/steps/${stepId}`);
  },

  async reorderSteps(processId: string, orderedIds: string[]): Promise<void> {
    await api.post(`/recruitment/processes/${processId}/steps/reorder`, { ordered_ids: orderedIds });
  },

  // ── Process instances ──
  async listInstances(
    query: Partial<ListProcessInstanceQuery> = {},
  ): Promise<PaginatedResponse<RecruitmentProcessInstance>> {
    const res = await api.get<PaginatedResponse<RecruitmentProcessInstance>>('/recruitment/instances', {
      params: query,
    });
    return res.data;
  },

  async getInstance(id: string): Promise<RecruitmentProcessInstanceDetail> {
    const res = await api.get<DataResponse<RecruitmentProcessInstanceDetail>>(`/recruitment/instances/${id}`);
    return res.data.data;
  },

  async startProcess(input: StartProcessInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/recruitment/instances', input);
    return res.data.data;
  },

  async toggleStep(instanceId: string, stepId: string, input: ToggleStepInput): Promise<void> {
    await api.post(`/recruitment/instances/${instanceId}/steps/${stepId}/toggle`, input);
  },

  async cancelProcess(instanceId: string, reason: string): Promise<void> {
    await api.post(`/recruitment/instances/${instanceId}/cancel`, { reason });
  },
};
